import {
	BeforeInsert,
	BeforeUpdate,
	Entity,
	Index,
	JoinColumn,
	ManyToOne,
	OneToMany,
	OneToOne,
} from 'typeorm';
import { Column } from '../../decorators';
import {
	AllowedShippingMethods,
	ClientAllowedPaymentMethods,
	ClientNightTimeEligibilityEnum,
	ClientNotificationPreferences,
} from '../../types';
import type { CycleFrequency } from '../../utils/cycle-cadence';
import { parsePhoneWithExtension } from '../../utils/validation';
import { BaseEntity } from './base.entity';
import { ClientAddressEntity } from './client-address.entity';
import { ClientAssignmentHistoryEntity } from './client-assignment-history.entity';
import { ClientCategoryEntity } from './client-category.entity';
import { ClientCompanyEntity } from './client-company.entity';
import { ClientContactEntity } from './client-contact.entity';
import { ClientFileEntity } from './client-file.entity';
import { ClientGroupEntity } from './client-group.entity';
import { ClientHoursEntity } from './client-hours.entity';
import { ClientInquiryEntity } from './client-inquiry.entity';
import { ClientItemEntity } from './client-item.entity';
import { ClientLifecycleStageEntity } from './client-lifecycle-stage.entity';
import { ClientListClientEntity } from './client-list-client.entity';
import { ClientNoteEntity } from './client-note.entity';
import { ClientPhoneEntity } from './client-phone.entity';
import { ClientPublicUserEntity } from './client-public-user.entity';
import { ClientRegistrationExistingEntity } from './client-registration-existing.entity';
import { ClientRegistrationNewEntity } from './client-registration-new.entity';
import { ClientSalesEntity } from './client-sales.entity';
import { ClientSpecialtyEntity } from './client-specialty.entity';
import { ClientUserEntity } from './client-user.entity';
import { ClientVisitEntity } from './client-visit.entity';
import { ClientWarehouseEntity } from './client-warehouse.entity';
import { ClientWholesalerEntity } from './client-wholesaler.entity';
import { ClientWorkdUserEntity } from './client-workd-user.entity';
import { CompanyEntity } from './company.entity';
import { CompetitivePriceEntity } from './competitive-price.entity';
import { EventClientEntity } from './event-client.entity';
import { InvoiceEntity } from './invoice.entity';
import { MPaymentPaymentMethodEntity } from './m-payment-payment-method.entity';
import { MPaymentTransactionEntity } from './m-payment-transaction.entity';
import { MSmClientOpportunityEntity } from './m-sm-client-opportunity.entity';
import { OrderEntity } from './order.entity';
import { OwnershipEntity } from './ownership.entity';
import { PriceContractClientEntity } from './price-contract-client.entity';
import { TermsCodeEntity } from './terms-code.entity';
import { UserEntity } from './user.entity';

/**
 * PD-15848 — when Workd collects from a client automatically.
 *
 * - `charge_on_delivery`: today's behaviour. The delivery charges the
 *   order's saved card / ACH at stop completion.
 * - `bill_on_terms`: each invoice is charged on its own due date.
 * - `cycle_billing`: the account's open invoices are consolidated into
 *   one charge per cadence.
 *
 * NULL on the client resolves to `charge_on_delivery`, as does any value
 * at all when the tenant setting `payments.billing_modes` is off.
 */
export type ClientBillingMode =
	| 'charge_on_delivery'
	| 'bill_on_terms'
	| 'cycle_billing';

@Index('_fk_user__id_alternate', ['fkUserIdAlternate'], {})
@Index(
	'client__fk_client_address__billing_fk',
	['fkClientAddressIdBilling'],
	{},
)
@Index(
	'client__fk_client_address__id_shipping_fk',
	['fkClientAddressIdShipping'],
	{},
)
@Index('client__fk_client_contact__id_fk', ['fkClientContactId'], {})
@Index('client__fk_client_note__id_fk', ['fkClientNoteId'], {})
@Index('client__fk_invoice__id_fk', ['fkInvoiceId'], {})
@Index('client__fk_order__id_fk', ['fkOrderId'], {})
@Index('client__fk_user__id_fk', ['fkUserId'], {})
@Index(
	'client_fk_client_registration_new__id_fk',
	['fkClientRegistrationNewId'],
	{},
)
@Index(
	'externalId_fk_company__id_primary',
	['externalId', 'fkCompanyIdPrimary'],
	{ unique: true },
)
@Index('fk_company__id_primary', ['fkCompanyIdPrimary'], {})
@Index('fk_invoice__id_last', ['fkInvoiceIdLast'], {})
@Index('fk_order__id_last', ['fkOrderIdLast'], {})
@Index('fk_trip_stop__id_last', ['fkTripStopIdLast'], {})
@Index(
	'fx_client_fulltext',
	[
		'name',
		'address1',
		'address2',
		'city',
		'state',
		'zip',
		'phone',
		'fax',
		'email',
		'country',
		'externalId',
		'externalId2',
	],
	{ fulltext: true },
)
@Index('idx_client_country', ['country'], {})
@Index(
	'idx_client_geography',
	['standardizedCity', 'standardizedState', 'country'],
	{},
)
@Index('idx_client_name_fulltext', ['name'], { fulltext: true })
@Index('idx_client_state_city', ['standardizedCity', 'standardizedState'], {})
@Index('idx_client_deleted_owner', ['deletedDate', 'ownerUserId'], {})
@Index('idx_client_license_match_status', ['licenseMatchStatus'], {})
// Lookups used by the lead registration dedup pass — exact-match
// short-circuits to skip a full table scan on every public submit.
@Index('idx_client_email_dedup', ['email'], {})
@Index('idx_client_email2_dedup', ['email2'], {})
@Index('idx_client_phone_dedup', ['phone'], {})
@Index('idx_client_creation_source', ['creationSource'], {})
@Index('idx_client_potential_duplicate', ['fkClientIdPotentialDuplicate'], {})
@Index('idx_client_parent', ['fkClientIdParent'], {})
@Index('idx_client_billing_account', ['fkClientIdBilling'], {})
@Index('idx_client_ownership', ['fkOwnershipId'], {})
// PD-16821 — sales planning searches by proximity (a latitude/longitude
// bounding box ahead of the distance test, see `buildGeoRadiusSql`) and by
// days since the last visit. Both are full scans without these.
@Index('idx_client_lat_lng', ['latitude', 'longitude'], {})
@Index('idx_client_last_visit_date', ['lastVisitDate'], {})
@Entity('client')
export class ClientEntity extends BaseEntity {
	@Column('varchar', { name: 'externalId', nullable: true, length: 64 })
	public externalId!: string | null;

	@Column('varchar', { name: 'externalId2', nullable: true, length: 64 })
	public externalId2!: string | null;

	/**
	 * PD-15452 — archived clients leave the client lists by default (their
	 * order/invoice history stays intact and visible). NULL = not archived.
	 * Distinct from soft-delete: delete is reserved for clients with no
	 * order/invoice history; archive is the path for everything else.
	 */
	@Column('datetime', { name: 'archivedDate', nullable: true })
	public archivedDate!: Date | null;

	/**
	 * PD-15449 — per-client override of the route-level "Deliver During
	 * Business Hours Only" setting. NULL = follow the route's setting;
	 * TRUE = always enforce business hours for this client's stops;
	 * FALSE = never enforce (exempt), regardless of the route.
	 */
	@Column('tinyint', {
		name: 'deliverDuringBusinessHoursOnly',
		nullable: true,
		width: 1,
	})
	public deliverDuringBusinessHoursOnly!: boolean | null;

	@Column(
		'varchar',
		{ name: 'leadOrCustomer', length: 255 },
		{
			selectOptions: [
				{ text: 'Customer', value: 'Customer' },
				{ text: 'Lead', value: 'Lead' },
			],
		},
	)
	public leadOrCustomer!: string;

	@Column('varchar', { name: 'name', nullable: true, length: 255 })
	public name!: string | null;

	@Column('varchar', { name: 'dba', nullable: true, length: 255 })
	public dba!: string | null;

	@Column('varchar', { name: 'legalName', nullable: true, length: 255 })
	public legalName!: string | null;

	@Column('varchar', { name: 'businessType', nullable: true, length: 255 })
	public businessType!: string | null;

	@Column('int', { name: '_fk_user__id', nullable: true, unsigned: true })
	public fkUserId!: number | null;

	@Column('varchar', { name: 'salesStatus', nullable: true, length: 64 })
	public salesStatus!: string | null;

	@Column('varchar', { name: 'address1', nullable: true, length: 255 })
	public address1!: string | null;

	@Column('varchar', { name: 'address2', nullable: true, length: 255 })
	public address2!: string | null;

	@Column('varchar', { name: 'city', nullable: true, length: 255 })
	public city!: string | null;

	@Column('varchar', { name: 'state', nullable: true, length: 255 })
	public state!: string | null;

	@Column('varchar', { name: 'zip', nullable: true, length: 255 })
	public zip!: string | null;

	@Column('varchar', {
		name: 'country',
		nullable: true,
		length: 2,
		default: () => "'US'",
	})
	public country!: string | null;

	@Column('varchar', { name: 'phone', nullable: true, length: 255 })
	public phone!: string | null;

	// Phone extension, stored separately from the E.164 `phone` value because
	// E.164 cannot encode an extension. Populated by `_normalizePhone` when the
	// phone input carries a trailing extension. PD-14494.
	@Column('varchar', { name: 'extension', nullable: true, length: 64 })
	public extension!: string | null;

	@Column('varchar', { name: 'fax', nullable: true, length: 255 })
	public fax!: string | null;

	@Column('varchar', { name: 'email', nullable: true, length: 255 })
	public email!: string | null;

	@Column('varchar', { name: 'email2', nullable: true, length: 255 })
	public email2!: string | null;

	@Column('int', { name: 'scriptsPerDay', nullable: true })
	public scriptsPerDay!: number | null;

	@Column('tinyint', { name: 'isDoNotCall', width: 1, default: () => "'0'" })
	public isDoNotCall!: boolean;

	@Column('tinyint', { name: 'isDoNotFax', width: 1, default: () => "'0'" })
	public isDoNotFax!: boolean;

	/**
	 * Whether statement runs (scheduled or manual) generate + send statements
	 * for this client (PD-14075). DEFAULT OFF — statements are opt-in per
	 * client. One-off generation from the client's Statements tab ignores
	 * this (explicit user action).
	 */
	@Column('tinyint', {
		name: 'receiveStatements',
		width: 1,
		default: () => "'0'",
	})
	public receiveStatements!: boolean;

	/**
	 * Per-client statement schedule override — an RRULE (the same recurrence
	 * language tasks use). When set, the client is excluded from the
	 * company-schedule run and gets a client-scoped run when their own rule
	 * fires; null = company/tenant schedule.
	 */
	@Column('varchar', {
		name: 'statementRecurrenceRule',
		nullable: true,
		length: 255,
	})
	public statementRecurrenceRule!: string | null;

	/**
	 * Whether the statement email goes to the customer's email on file
	 * ("Customer email on file" on Edit Customer › Statements). Default ON:
	 * receiveStatements is the opt-in gate. PD-16902 narrowed this from "email
	 * statements at all" to one of three recipient choices; see
	 * `statementAddressRecipients` in libs/shared/src/utils/statement-email-recipients.ts.
	 */
	@Column('tinyint', {
		name: 'sendStatementEmail',
		width: 1,
		default: () => "'1'",
	})
	public sendStatementEmail!: boolean;

	/**
	 * PD-16902 — whether the customer's storefront users get each statement:
	 * the statement email and the "statement ready" text (PD-16863), sent to
	 * everyone the storefront lets open the account, other than users who
	 * turned that channel off in their storefront notification settings. The
	 * bell notice is not affected. Default ON (owner decision): HF wants every
	 * storefront user to get their statements.
	 */
	@Column('tinyint', {
		name: 'statementPortalUsers',
		width: 1,
		default: () => "'1'",
	})
	public statementPortalUsers!: boolean;

	/**
	 * PD-16902 — other addresses that get every statement email, such as a
	 * shared accounts-payable mailbox. Replaces the single statement email
	 * override, which went to that address INSTEAD of the email on file; the
	 * migration that removed it unticked the email on file for those customers
	 * so their statements keep going to the same place.
	 */
	@Column('json', {
		name: 'statementEmailAddresses',
		nullable: true,
	})
	public statementEmailAddresses!: Array<string> | null;

	@Column('int', { name: 'creditLimit', nullable: true })
	public creditLimit!: number | null;

	@Column('decimal', {
		name: 'creditBalance',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public creditBalance!: string | null;

	@Column('tinyint', { name: 'hasCreditHold', width: 1, default: () => "'0'" })
	public hasCreditHold!: boolean;

	@Column('tinyint', { name: 'hasSaturdayDelivery', nullable: true, width: 1 })
	public hasSaturdayDelivery!: boolean | null;

	/**
	 * Tri-state route eligibility used by the distribution engine.
	 * See {@link ClientNightTimeEligibilityEnum}. Defaults to DAY_ONLY
	 * so a new client is never silently scheduled on a night route.
	 */
	@Column('enum', {
		name: 'nightTimeEligibility',
		enum: ClientNightTimeEligibilityEnum,
		default: ClientNightTimeEligibilityEnum.DAY_ONLY,
	})
	public nightTimeEligibility!: ClientNightTimeEligibilityEnum;

	/**
	 * When true, shop/cart checkout must reject orders whose line
	 * quantities are not a whole multiple of the item's
	 * `unitsPerPallet`. Pallet-only customers force full-pallet
	 * picks at the warehouse.
	 */
	@Column('tinyint', {
		name: 'isFullPalletOnly',
		width: 1,
		default: () => "'0'",
	})
	public isFullPalletOnly!: boolean;

	/**
	 * When true, the orders-wizard / sensor-fill pipeline is allowed
	 * to generate orders for this client automatically based on
	 * fill-level + reorder-point logic. When false, orders for this
	 * client are always operator-initiated.
	 */
	@Column('tinyint', {
		name: 'isAutoGenerateOrders',
		width: 1,
		default: () => "'0'",
	})
	public isAutoGenerateOrders!: boolean;

	/**
	 * PD-13749 — when true, this customer requires a captured signature on
	 * delivery: the driver's stop signature step makes the signature mandatory
	 * (no "No signature available" skip) and shows an orange notice. Defaults
	 * to false, preserving the prior behavior where signing is optional.
	 */
	@Column('tinyint', {
		name: 'requiresDeliverySignature',
		width: 1,
		default: () => "'0'",
	})
	public requiresDeliverySignature!: boolean;

	/**
	 * PD-14982 — the customer's standing delivery instructions: opening hours,
	 * where to leave product when the site is closed, access notes. Most
	 * delivery instructions are a property of the CUSTOMER rather than of a
	 * one-off order, so they were being retyped on every order.
	 *
	 * This is a DEFAULT, not the value the driver reads. At order creation it
	 * is snapshotted into an `order_note` of type `OrderNoteType.DELIVERY`,
	 * which stays editable per order. Editing the default deliberately does
	 * NOT propagate to existing orders — that would change what a driver was
	 * told after the fact.
	 */
	@Column('text', { name: 'defaultDeliveryInstructions', nullable: true })
	public defaultDeliveryInstructions!: string | null;

	@Column('datetime', { name: 'lastAssignedDate', nullable: true })
	public lastAssignedDate!: Date | null;

	@Column('datetime', { name: 'lastCallDate', nullable: true })
	public lastCallDate!: Date | null;

	@Column('datetime', { name: 'lastImportDate', nullable: true })
	public lastImportDate!: Date | null;

	@Column('varchar', {
		name: 'standardizedAddress1',
		nullable: true,
		length: 255,
	})
	public standardizedAddress1!: string | null;

	@Column('varchar', {
		name: 'standardizedAddress2',
		nullable: true,
		length: 255,
	})
	public standardizedAddress2!: string | null;

	@Column('varchar', { name: 'standardizedCity', nullable: true, length: 255 })
	public standardizedCity!: string | null;

	@Column('varchar', { name: 'standardizedState', nullable: true, length: 255 })
	public standardizedState!: string | null;

	@Column('varchar', { name: 'standardizedZip', nullable: true, length: 255 })
	public standardizedZip!: string | null;

	@Column('varchar', { name: 'county', nullable: true, length: 255 })
	public county!: string | null;

	@Column('int', {
		name: 'fk_client_registration_new__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientRegistrationNewId!: number | null;

	@Column('datetime', { name: 'lastOrderDate', nullable: true })
	public lastOrderDate!: Date | null;

	@Column('date', { name: 'lastInvoiceDate', nullable: true })
	public lastInvoiceDate!: Date | null;

	@Column('int', { name: 'npiNumber', nullable: true })
	public npiNumber!: number | null;

	@Column('varchar', { name: 'ncpdpId', nullable: true, length: 255 })
	public ncpdpId!: string | null;

	@Column('varchar', { name: 'salesTaxId', nullable: true, length: 255 })
	public salesTaxId!: string | null;

	@Column('varchar', { name: 'federalEin', nullable: true, length: 255 })
	public federalEin!: string | null;

	@Column('varchar', { name: 'defaultCarrier', nullable: true, length: 255 })
	public defaultCarrier!: string | null;

	@Column('varchar', { name: 'paymentMethod', nullable: true, length: 255 })
	public paymentMethod!: string | null;

	@Column('varchar', { name: 'paymentTerm', nullable: true, length: 255 })
	public paymentTerm!: string | null;

	/**
	 * PD-15847 — the client's default payment method when that default is a
	 * RECORDLESS type (Company Credit / Cash / Check). Those have no saved
	 * `m_payment_payment_method` row to flag Primary, so they could not be
	 * expressed as a default at all before this column existed.
	 *
	 * Holds a `PaymentMethodTypeEnum` value. NULL behaves exactly as before:
	 * the saved Primary row is the default. Resolution (recordless wins,
	 * else the Primary) lives in `resolveClientDefaultPaymentMethod` — read
	 * it through that helper, never directly.
	 */
	@Column('varchar', {
		name: 'defaultPaymentMethodType',
		nullable: true,
		length: 64,
		comment:
			'Recordless default payment method type (Company Credit / Cash / Check). NULL = use the saved Primary method.',
	})
	public defaultPaymentMethodType!: string | null;

	/**
	 * PD-15848 — WHEN Workd collects from this client automatically.
	 *
	 * `charge_on_delivery` is today's behaviour and the value a NULL
	 * column resolves to, so every existing client is unchanged. The two
	 * on-account modes stop the delivery-time charge (the enforcement is
	 * in `OrderInvoiceBillingService.collectForOrderInvoice`, order-method
	 * instrument only) and are collected by a system-owned payment
	 * schedule instead: `bill_on_terms` charges each invoice on its own
	 * due date, `cycle_billing` consolidates the account's open invoices
	 * into one charge per cadence.
	 *
	 * Only meaningful when the tenant's `payments.billing_modes` instance
	 * setting is on; with it off the stored value is IGNORED and the
	 * client behaves as `charge_on_delivery`. Read it through
	 * `resolveEffectiveBillingMode`, never directly.
	 */
	@Column('varchar', {
		name: 'billingMode',
		nullable: true,
		length: 32,
		comment:
			'Billing mode: charge_on_delivery (default / NULL), bill_on_terms, cycle_billing. Only honoured when payments.billing_modes is on.',
	})
	public billingMode!: ClientBillingMode | null;

	/**
	 * PD-15848 — the client's cycle cadence, when `billingMode` is
	 * `cycle_billing`. The cycle SCHEDULE row is derived from these two
	 * columns rather than hand-built, so the client editor stays the one
	 * place a cycle is configured.
	 */
	@Column('varchar', {
		name: 'billingCycleFrequency',
		nullable: true,
		length: 16,
		comment: 'Cycle cadence: weekly, biweekly or monthly.',
	})
	public billingCycleFrequency!: CycleFrequency | null;

	/**
	 * The cadence anchor: day of month (1-28) for a monthly cycle, day of
	 * week (0 = Sunday … 6 = Saturday) for weekly / biweekly. One column
	 * because only one of the two is ever meaningful, and which one is
	 * decided by `billingCycleFrequency`.
	 */
	@Column('smallint', {
		name: 'billingCycleDay',
		nullable: true,
		comment:
			'Cycle anchor: dayOfMonth 1-28 when monthly, dayOfWeek 0-6 when weekly/biweekly.',
	})
	public billingCycleDay!: number | null;

	/**
	 * PD-15848 — the saved Credit Card / ACH row an automatic billing mode
	 * collects with. Kept separate from the Primary flag because the
	 * instrument a cycle charges is a deliberate choice that should not
	 * move when somebody changes which card the counter uses by default;
	 * when it is NULL the Primary stands in.
	 */
	@Column('int', {
		name: 'fk_m_payment_payment_method__id_billing',
		nullable: true,
		unsigned: true,
		comment:
			'Saved Credit Card / ACH row used by bill_on_terms and cycle_billing. NULL = fall back to the saved Primary.',
	})
	public fkPaymentMethodIdBilling!: number | null;

	@ManyToOne(() => MPaymentPaymentMethodEntity, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([
		{
			name: 'fk_m_payment_payment_method__id_billing',
			referencedColumnName: 'id',
		},
	])
	public fkPaymentMethodBilling!: MPaymentPaymentMethodEntity | null;

	@Column('int', {
		name: 'fk_terms_code__id',
		nullable: true,
		unsigned: true,
	})
	public fkTermsCodeId!: number | null;

	@ManyToOne(() => TermsCodeEntity, (termsCode) => termsCode.clients, {
		onDelete: 'SET NULL',
		onUpdate: 'CASCADE',
	})
	@JoinColumn([{ name: 'fk_terms_code__id', referencedColumnName: 'id' }])
	public fkTermsCode!: TermsCodeEntity | null;

	@Column('int', {
		name: '_fk_user__id_alternate',
		nullable: true,
		unsigned: true,
	})
	public fkUserIdAlternate!: number | null;

	@Column('int', { name: '_fk_invoice__id', nullable: true, unsigned: true })
	public fkInvoiceId!: number | null;

	@Column('int', { name: '_fk_order__id', nullable: true, unsigned: true })
	public fkOrderId!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_shipping',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdShipping!: number | null;

	@Column('int', {
		name: '_fk_client_address__id_billing',
		nullable: true,
		unsigned: true,
	})
	public fkClientAddressIdBilling!: number | null;

	@Column('int', {
		name: '_fk_client_note__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientNoteId!: number | null;

	@Column('int', {
		name: '_fk_client_contact__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientContactId!: number | null;

	@Column('int', {
		name: 'fk_invoice__id_last',
		nullable: true,
		unsigned: true,
	})
	public fkInvoiceIdLast!: number | null;

	@Column('int', { name: 'fk_order__id_last', nullable: true, unsigned: true })
	public fkOrderIdLast!: number | null;

	// PD-15698 — pointer to the client's most recent COMPLETED trip stop,
	// maintained by the stop-completion flows (same denormalized "last X"
	// pattern as fk_invoice__id_last / fk_order__id_last) so lists can show
	// and sort by the last visit without an aggregate over trip_stop.
	@Column('int', {
		name: 'fk_trip_stop__id_last',
		nullable: true,
		unsigned: true,
	})
	public fkTripStopIdLast!: number | null;

	@Column('decimal', {
		name: 'm_sm_latitude',
		nullable: true,
		precision: 10,
		scale: 8,
	})
	public latitude!: string | null;

	@Column('decimal', {
		name: 'm_sm_longitude',
		nullable: true,
		precision: 11,
		scale: 8,
	})
	public longitude!: string | null;

	/** PD-16090 — which geocoder placed the coordinates ('census' | 'google'), null when unknown or set by hand. */
	@Column('varchar', { name: 'geocodeProvider', nullable: true, length: 16 })
	public geocodeProvider!: string | null;

	@Column('varchar', { name: 'googlePlaceId', nullable: true, length: 255 })
	public googlePlaceId!: string | null;

	@Column('date', { name: 'lastContactDate', nullable: true })
	public lastContactDate!: string | null;

	/**
	 * PD-15918 (decision 3) — denormalised "when did a rep last see this
	 * account": MAX(m_sm_client_visit.m_sm_date) over live visits. Raised by
	 * visit create, recomputed by visit delete, backfilled by migration, so
	 * list filters need no join.
	 */
	@Column('datetime', { name: 'lastVisitDate', nullable: true })
	public lastVisitDate!: Date | null;

	/**
	 * PD-16083 — how often a rep should see this account (weekly, biweekly,
	 * monthly, quarterly, none). Null means "use the tenant default for its
	 * kind" (`sales_routes.default_visit_cadence`). Set inline from the
	 * planning lists; read by coverage on every map.
	 */
	@Column('varchar', { name: 'visitCadence', nullable: true, length: 20 })
	public visitCadence!: string | null;

	/**
	 * PD-15918 (decision 18) — GREATEST of three delivery signals, each
	 * ignored when null or in the future: latest completed client trip_stop
	 * arrival, latest shipment.date (via shipment_detail → invoice/order),
	 * latest invoice.date. Maintained by stop completion, the invoice and
	 * shipment-detail subscribers and the client-item integration; backfilled
	 * by migration.
	 */
	@Column('datetime', { name: 'lastDeliveryDate', nullable: true })
	public lastDeliveryDate!: Date | null;

	@Column('decimal', {
		name: 'averageInvoiceRevenue',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public averageInvoiceRevenue!: string | null;

	@Column('decimal', {
		name: 'invoiceFrequency',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public invoiceFrequency!: string | null;

	@Column('datetime', { name: 'nextActivityDate', nullable: true })
	public nextActivityDate!: Date | null;

	@Column('int', {
		name: 'contractPricingRule',
		unsigned: true,
		default: () => "'1'",
	})
	public contractPricingRule!: number;

	@Column('date', { name: 'conversionDate', nullable: true })
	public conversionDate!: string | null;

	@Column('date', { name: 'deactivationDate', nullable: true })
	public deactivationDate!: string | null;

	@Column('decimal', {
		name: 'forecast',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public forecast!: string | null;

	@Column('decimal', {
		name: 'likelihood',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public likelihood!: string | null;

	@Column('date', { name: 'forecastedReorderDate', nullable: true })
	public forecastedReorderDate!: string | null;

	@Column('decimal', {
		name: 'revenueTrend360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrend360!: string | null;

	@Column('decimal', {
		name: 'revenueTrendDiff360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrendDiff360!: string | null;

	@Column('decimal', {
		name: 'revenueTrend90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrend90!: string | null;

	@Column('decimal', {
		name: 'revenueTrendDiff90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public revenueTrendDiff90!: string | null;

	@Column('decimal', {
		name: 'uniqueItemsTrend360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public uniqueItemsTrend360!: string | null;

	@Column('decimal', {
		name: 'uniqueItemsTrendDiff360',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public uniqueItemsTrendDiff360!: string | null;

	@Column('decimal', {
		name: 'uniqueItemsTrend90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public uniqueItemsTrend90!: string | null;

	@Column('decimal', {
		name: 'uniqueItemsTrendDiff90',
		nullable: true,
		precision: 14,
		scale: 10,
	})
	public uniqueItemsTrendDiff90!: string | null;

	@Column('decimal', {
		name: 'minimumTrendDiff',
		precision: 14,
		scale: 10,
		default: () => "'0.0000000000'",
	})
	public minimumTrendDiff!: string;

	@Column('int', {
		name: 'fk_company__id_primary',
		unsigned: true,
		default: () => "'1'",
	})
	public fkCompanyIdPrimary!: number;

	@Column('int', {
		name: 'fk_client_lifecycle_stage__id',
		nullable: true,
		unsigned: true,
	})
	public fkClientLifecycleStageId!: number | null;

	@Column('int', {
		name: 'lifecycle_ordinal',
		unsigned: true,
		default: () => "'0'",
	})
	public lifecycleOrdinal!: number;

	@Column('decimal', {
		name: 'totalGPAggregate',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpAggregate!: string | null;

	@Column('decimal', {
		name: 'totalGPMargin',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public totalGpMargin!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregate90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregate90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMargin90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMargin90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenue90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenue90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPAggregateTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpAggregateTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingTotalGPMarginTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingTotalGpMarginTrend90!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend30',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend30!: string | null;

	@Column('decimal', {
		name: 'rollingRevenueTrend90',
		nullable: true,
		precision: 13,
		scale: 2,
	})
	public rollingRevenueTrend90!: string | null;

	@Column('json', {
		name: 'customFields',
		nullable: true,
		comment: 'JSON key-value pairs for tenant-specific custom fields.',
	})
	public customFields!: Record<string, unknown> | null;

	@Column('json', {
		name: 'allowedPaymentMethods',
		nullable: true,
		comment:
			'JSON structured object indicating what payment methods are allowed and associated fees for each.',
	})
	public allowedPaymentMethods!: ClientAllowedPaymentMethods | null;

	/**
	 * Per-event notification channel toggles + default SMS / email
	 * overrides. See {@link ClientNotificationPreferences}. When
	 * null, no overrides apply — channels are OFF and the
	 * primary `phone` / `email` fields are used.
	 */
	@Column('json', {
		name: 'notificationPreferences',
		nullable: true,
		comment:
			'Per-event notification channel toggles + default SMS/email override.',
	})
	public notificationPreferences!: ClientNotificationPreferences | null;

	@Column('datetime', { name: 'npi_verified_at', nullable: true })
	public npiVerifiedAt!: Date | null;

	@Column('varchar', { name: 'npi_org_name', nullable: true, length: 500 })
	public npiOrgName!: string | null;

	@Column('enum', {
		name: 'license_match_status',
		enum: [
			'unmatched',
			'auto_matched',
			'partially_matched',
			'confirmed',
			'no_license_found',
		],
		nullable: true,
		default: 'unmatched',
	})
	public licenseMatchStatus!: string | null;

	@Column('decimal', {
		name: 'license_match_score',
		nullable: true,
		precision: 5,
		scale: 2,
	})
	public licenseMatchScore!: string | null;

	@Column('datetime', { name: 'last_match_run_at', nullable: true })
	public lastMatchRunAt!: Date | null;

	@Column('json', {
		name: 'allowedShippingMethods',
		nullable: true,
		comment:
			'JSON structured object indicating what shipping methods are allowed and associated fees for each.',
	})
	public allowedShippingMethods!: AllowedShippingMethods | null;

	// Customer-provided answer to "How did you hear about us?" on a
	// self-serve registration form. Free text — different tenants will
	// expose different prompts (e.g. trade show name, referrer name,
	// channel). Distinct from creationSource, which is the system channel
	// that created the row.
	@Column('varchar', { name: 'leadSource', nullable: true, length: 255 })
	public leadSource!: string | null;

	// System-set channel identifying how this client row was created —
	// 'portal_registration' | 'external_embed' | 'manual' | 'csv_import' |
	// 'api'. Set by the controller/service that owns the insert path.
	// Used to attribute lead origin and for ops/analytics reporting; never
	// shown directly to a buyer.
	@Column('varchar', { name: 'creationSource', nullable: true, length: 64 })
	public creationSource!: string | null;

	// Pointer to an existing client whose email or phone matched at lead
	// registration time. Null when no match was found OR after staff have
	// reviewed/dismissed the flag. Single FK is sufficient — if multiple
	// clients matched, the strongest match wins. Reviewers can still see
	// all matches via the merge dialog.
	@Column('int', {
		name: 'fk_client__id_potential_duplicate',
		nullable: true,
		unsigned: true,
	})
	public fkClientIdPotentialDuplicate!: number | null;

	// Parent account link — points at the umbrella client this one belongs
	// to (e.g. a chain owner over its stores). The hierarchy is capped at
	// two levels: a client with children cannot itself get a parent, and a
	// client that has a parent cannot be chosen as one. That cap is a
	// business rule enforced at assignment time (ClientHierarchyService),
	// not by the schema, so deeper trees stay possible later without a
	// schema change.
	@Column('int', {
		name: 'fk_client__id_parent',
		nullable: true,
		unsigned: true,
	})
	public fkClientIdParent!: number | null;

	/**
	 * PD-15849 — "Bill with parent": the client whose receivable this
	 * client's orders and invoices belong to.
	 *
	 * The only value this phase permits is the client's own parent
	 * (`fkClientIdParent`); a pointer that disagrees with the current
	 * parent is refused rather than followed, so a parent reassignment
	 * cannot silently route money to a third account. NULL means the
	 * client is its own billing account, which is every existing client.
	 *
	 * Read it through `resolveBillingAccountId` / `isBilledWithParent`
	 * (`libs/shared/src/utils/billing-account.ts`), never directly.
	 */
	@Column('int', {
		name: 'fk_client__id_billing',
		nullable: true,
		unsigned: true,
		comment:
			"Bill-with-parent pointer: the parent account that carries this client's receivables. NULL = bills itself.",
	})
	public fkClientIdBilling!: number | null;

	/**
	 * PD-15849 (deferred from PD-15708 §4.9) — the account is on billing
	 * hold: an automatic collection for it exhausted its retries and a
	 * human has to look at it.
	 *
	 * Set on the BILLING ACCOUNT (not the ordering client) when a cycle
	 * occurrence reaches terminal failure, and cleared when a later
	 * release for that account succeeds. Advisory only in this phase: it
	 * drives visibility and notification, it does not block order entry.
	 */
	@Column('tinyint', {
		name: 'billingHold',
		width: 1,
		default: () => "'0'",
		comment:
			'Automatic billing for this account failed terminally and needs review.',
	})
	public billingHold!: boolean;

	// PD-14182: ownership grouping — links this client to an ownership record
	// (a named owner / holding group). Independent of the parent/child
	// hierarchy: a client can have both a parent account and an ownership.
	// It's a flat grouping (a client points at a single ownership), so there
	// is no depth cap to enforce like the parent/child two-level rule.
	@Column('int', {
		name: 'fk_ownership__id',
		nullable: true,
		unsigned: true,
	})
	public fkOwnershipId!: number | null;

	@ManyToOne(
		() => ClientRegistrationNewEntity,
		(clientRegistrationNew) => clientRegistrationNew.clients,
		{ onDelete: 'RESTRICT', onUpdate: 'RESTRICT' },
	)
	@JoinColumn([
		{ name: 'fk_client_registration_new__id', referencedColumnName: 'id' },
	])
	public fkClientRegistrationNew!: ClientRegistrationNewEntity;

	@ManyToOne(() => UserEntity, (user) => user.clientsAssignedAsAlternate, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id_alternate', referencedColumnName: 'id' }])
	public fkUserIdAlternate2!: UserEntity;

	@ManyToOne(() => InvoiceEntity, (invoice) => invoice.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_invoice__id_last', referencedColumnName: 'id' }])
	public fkInvoiceIdLast2!: InvoiceEntity;

	@ManyToOne(() => OrderEntity, (order) => order.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_order__id_last', referencedColumnName: 'id' }])
	public fkOrderIdLast2!: OrderEntity;

	@ManyToOne(() => CompanyEntity, (company) => company.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_company__id_primary', referencedColumnName: 'id' }])
	public fkCompanyIdPrimary2!: CompanyEntity;

	@ManyToOne(() => ClientLifecycleStageEntity, (stage) => stage.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([
		{ name: 'fk_client_lifecycle_stage__id', referencedColumnName: 'id' },
	])
	public fkClientLifecycleStage!: ClientLifecycleStageEntity | null;

	// PD-14182: ownership grouping relation (see fkOwnershipId).
	@ManyToOne(() => OwnershipEntity, (ownership) => ownership.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_ownership__id', referencedColumnName: 'id' }])
	public ownership!: OwnershipEntity | null;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.clients,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_billing', referencedColumnName: 'id' },
	])
	public fkClientAddressIdBilling2!: ClientAddressEntity;

	@ManyToOne(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.clients2,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([
		{ name: '_fk_client_address__id_shipping', referencedColumnName: 'id' },
	])
	public fkClientAddressIdShipping2!: ClientAddressEntity;

	@ManyToOne(
		() => ClientContactEntity,
		(clientContact) => clientContact.clients,
		{
			onDelete: 'RESTRICT',
			onUpdate: 'RESTRICT',
		},
	)
	@JoinColumn([{ name: '_fk_client_contact__id', referencedColumnName: 'id' }])
	public fkClientContact!: ClientContactEntity;

	@ManyToOne(() => ClientNoteEntity, (clientNote) => clientNote.clients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_client_note__id', referencedColumnName: 'id' }])
	public fkClientNote!: ClientNoteEntity;

	@ManyToOne(() => InvoiceEntity, (invoice) => invoice.clients2, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_invoice__id', referencedColumnName: 'id' }])
	public fkInvoice!: InvoiceEntity;

	@ManyToOne(() => OrderEntity, (order) => order.clients2, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_order__id', referencedColumnName: 'id' }])
	public fkOrder!: OrderEntity;

	@ManyToOne(() => UserEntity, (user) => user.assignedClients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: '_fk_user__id', referencedColumnName: 'id' }])
	public fkUser!: UserEntity;

	// Self-referential parent/child account pair (see fkClientIdParent).
	@ManyToOne(() => ClientEntity, (client) => client.childClients, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_parent', referencedColumnName: 'id' }])
	public parentClient!: ClientEntity | null;

	// PD-15849 — the billing account this client's documents are raised
	// against. Self-referential like `parentClient` and, in this phase,
	// always the same row; kept as its own relation because the pointer
	// is an explicit billing decision rather than a consequence of the
	// hierarchy.
	@ManyToOne(() => ClientEntity, {
		onDelete: 'RESTRICT',
		onUpdate: 'RESTRICT',
	})
	@JoinColumn([{ name: 'fk_client__id_billing', referencedColumnName: 'id' }])
	public billingClient!: ClientEntity | null;

	@OneToMany(() => ClientEntity, (client) => client.parentClient)
	public childClients!: Array<ClientEntity>;

	@OneToMany(() => ClientFileEntity, (clientFile) => clientFile.fkClient)
	public clientFiles!: Array<ClientFileEntity>;

	@OneToMany(
		() => ClientWorkdUserEntity,
		(clientWorkdUser) => clientWorkdUser.fkClient,
	)
	public clientWorkdUsers!: Array<ClientWorkdUserEntity>;

	@OneToMany(
		() => ClientAddressEntity,
		(clientAddress) => clientAddress.fkClient,
	)
	public clientAddresses!: Array<ClientAddressEntity>;

	@OneToMany(
		() => ClientAssignmentHistoryEntity,
		(clientAssignmentHistory) => clientAssignmentHistory.fkClient,
	)
	public clientAssignmentHistories!: Array<ClientAssignmentHistoryEntity>;

	@OneToMany(
		() => ClientCategoryEntity,
		(clientCategory) => clientCategory.fkClient,
	)
	public clientCategories!: Array<ClientCategoryEntity>;

	@OneToMany(
		() => ClientCompanyEntity,
		(clientCompany) => clientCompany.fkClient,
	)
	public clientCompanies!: Array<ClientCompanyEntity>;

	@OneToMany(
		() => ClientContactEntity,
		(clientContact) => clientContact.fkClient,
	)
	public clientContacts!: Array<ClientContactEntity>;

	@OneToMany(() => ClientGroupEntity, (clientGroup) => clientGroup.fkClient)
	public clientGroups!: Array<ClientGroupEntity>;

	@OneToOne(() => ClientHoursEntity, (clientHours) => clientHours.fkClient)
	public clientHours!: ClientHoursEntity;

	@OneToMany(
		() => ClientInquiryEntity,
		(clientInquiry) => clientInquiry.fkClient,
	)
	public clientInquiries!: Array<ClientInquiryEntity>;

	@OneToMany(() => ClientItemEntity, (clientItem) => clientItem.fkClient)
	public clientItems!: Array<ClientItemEntity>;

	@OneToMany(
		() => ClientListClientEntity,
		(clientListClient) => clientListClient.fkClient,
	)
	public clientListClients!: Array<ClientListClientEntity>;

	@OneToMany(() => ClientNoteEntity, (clientNote) => clientNote.fkClient)
	public clientNotes!: Array<ClientNoteEntity>;

	@OneToMany(() => ClientPhoneEntity, (clientPhone) => clientPhone.fkClient)
	public clientPhones!: Array<ClientPhoneEntity>;

	@OneToMany(
		() => ClientPublicUserEntity,
		(clientPublicUser) => clientPublicUser.fkClient,
	)
	public clientPublicUsers!: Array<ClientPublicUserEntity>;

	@OneToMany(
		() => ClientRegistrationExistingEntity,
		(clientRegistrationExisting) => clientRegistrationExisting.fkClient,
	)
	public clientRegistrationExistings!: Array<ClientRegistrationExistingEntity>;

	@OneToMany(
		() => ClientRegistrationNewEntity,
		(clientRegistrationNew) => clientRegistrationNew.fkClient,
	)
	public clientRegistrationNews!: Array<ClientRegistrationNewEntity>;

	@OneToMany(() => ClientSalesEntity, (clientSales) => clientSales.fkClient)
	public clientSales!: Array<ClientSalesEntity>;

	@OneToMany(
		() => ClientSpecialtyEntity,
		(clientSpecialty) => clientSpecialty.fkClient,
	)
	public clientSpecialties!: Array<ClientSpecialtyEntity>;

	@OneToMany(
		() => ClientWarehouseEntity,
		(clientWarehouse) => clientWarehouse.fkClient,
	)
	public clientWarehouses!: Array<ClientWarehouseEntity>;

	@OneToMany(
		() => ClientWholesalerEntity,
		(clientWholesaler) => clientWholesaler.fkClient,
	)
	public clientWholesalers!: Array<ClientWholesalerEntity>;

	@OneToMany(
		() => CompetitivePriceEntity,
		(competitivePrice) => competitivePrice.fkClient,
	)
	public competitivePrices!: Array<CompetitivePriceEntity>;

	@OneToMany(() => EventClientEntity, (eventClient) => eventClient.fkClient)
	public eventClients!: Array<EventClientEntity>;

	@OneToMany(() => InvoiceEntity, (invoice) => invoice.fkClient)
	public invoices!: Array<InvoiceEntity>;

	@OneToMany(
		() => MPaymentPaymentMethodEntity,
		(mPaymentPaymentMethod) => mPaymentPaymentMethod.mSmPaymentFkClient,
	)
	public paymentMethods!: Array<MPaymentPaymentMethodEntity>;

	@OneToMany(
		() => MPaymentTransactionEntity,
		(mPaymentTransaction) => mPaymentTransaction.mSmPaymentFkClient,
	)
	public transactions!: Array<MPaymentTransactionEntity>;

	@OneToMany(
		() => MSmClientOpportunityEntity,
		(mSmClientOpportunity) => mSmClientOpportunity.mSmFkClient,
	)
	public clientOpportunities!: Array<MSmClientOpportunityEntity>;

	@OneToMany(() => ClientVisitEntity, (clientVisit) => clientVisit.fkClient)
	public clientVisits!: Array<ClientVisitEntity>;

	@OneToMany(() => OrderEntity, (order) => order.fkClient)
	public orders!: Array<OrderEntity>;

	@OneToMany(
		() => PriceContractClientEntity,
		(priceContractClient) => priceContractClient.client,
	)
	public priceContractClients!: Array<PriceContractClientEntity>;

	@OneToMany(() => ClientUserEntity, (clientUser) => clientUser.fkClient)
	public clientUsers!: Array<ClientUserEntity>;

	// Write-path safety net: any code that bypasses the dedicated form
	// inputs (imports, scripts, bulk updates) gets phones normalized to
	// E.164 here so the caller-ID matcher's exact-string join doesn't
	// silently miss. If the value is unparseable (extensions, gibberish,
	// "see notes"), leave it alone — preserve user-entered data rather
	// than blank it.
	@BeforeInsert()
	@BeforeUpdate()
	// `protected` (not `private`): this hook is invoked by TypeORM via the
	// @BeforeInsert/@BeforeUpdate decorators, not by direct calls, so TS
	// `noUnusedLocals` (TS6133) would wrongly flag a `private` version as unused.
	protected _normalizePhone(): void {
		if (this.phone != null && this.phone !== '') {
			// Split a trailing extension into the dedicated `extension` column
			// (only when one wasn't set explicitly) and normalize the base
			// number to E.164. PD-14494.
			const { e164, extension } = parsePhoneWithExtension(this.phone);
			if (
				extension != null &&
				(this.extension == null || this.extension === '')
			) {
				this.extension = extension;
			}
			if (e164 != null) {
				this.phone = e164;
			}
		}
	}
}
